/**
 * Valnexo CRM - Biometric Authentication Hook
 * Permet l'authentification via Face ID / Touch ID / Code PIN sur mobile
 */

import { useState, useEffect, useCallback } from 'react';

// Check if WebAuthn is supported
const isWebAuthnSupported = () => {
    return window.PublicKeyCredential !== undefined;
};

// Check if we're in standalone PWA mode
const isStandalone = () => {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true;
};

// Check if we're on mobile
const isMobile = () => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

export function useBiometricAuth() {
    const [isAvailable, setIsAvailable] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        // Check if biometric auth is available
        const checkAvailability = async () => {
            if (!isWebAuthnSupported() || !isMobile()) {
                setIsAvailable(false);
                return;
            }

            try {
                const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
                setIsAvailable(available);
                
                // Check if already enabled
                const enabled = localStorage.getItem('valnexo_biometric_enabled') === 'true';
                setIsEnabled(enabled);
            } catch (e) {
                console.log('Biometric check failed:', e);
                setIsAvailable(false);
            }
        };

        checkAvailability();
    }, []);

    // Register biometric credential
    const enableBiometric = useCallback(async (userId, userEmail) => {
        if (!isAvailable) return { success: false, error: 'Biometric not available' };
        
        setIsLoading(true);
        try {
            const challenge = new Uint8Array(32);
            window.crypto.getRandomValues(challenge);

            const credential = await navigator.credentials.create({
                publicKey: {
                    challenge,
                    rp: {
                        name: 'Valnexo CRM',
                        id: window.location.hostname
                    },
                    user: {
                        id: Uint8Array.from(userId, c => c.charCodeAt(0)),
                        name: userEmail,
                        displayName: userEmail.split('@')[0]
                    },
                    pubKeyCredParams: [
                        { alg: -7, type: 'public-key' },   // ES256
                        { alg: -257, type: 'public-key' }  // RS256
                    ],
                    authenticatorSelection: {
                        authenticatorAttachment: 'platform',
                        userVerification: 'required',
                        residentKey: 'preferred'
                    },
                    timeout: 60000,
                    attestation: 'none'
                }
            });

            if (credential) {
                // Store credential info
                const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
                localStorage.setItem('valnexo_credential_id', credentialId);
                localStorage.setItem('valnexo_biometric_enabled', 'true');
                localStorage.setItem('valnexo_biometric_user', userEmail);
                setIsEnabled(true);
                return { success: true };
            }
        } catch (e) {
            console.error('Biometric registration failed:', e);
            return { success: false, error: e.message };
        } finally {
            setIsLoading(false);
        }
        
        return { success: false, error: 'Unknown error' };
    }, [isAvailable]);

    // Authenticate with biometric
    const authenticateWithBiometric = useCallback(async () => {
        if (!isEnabled) return { success: false, error: 'Biometric not enabled' };
        
        setIsLoading(true);
        try {
            const credentialId = localStorage.getItem('valnexo_credential_id');
            if (!credentialId) {
                throw new Error('No credential found');
            }

            const challenge = new Uint8Array(32);
            window.crypto.getRandomValues(challenge);

            const credential = await navigator.credentials.get({
                publicKey: {
                    challenge,
                    allowCredentials: [{
                        id: Uint8Array.from(atob(credentialId), c => c.charCodeAt(0)),
                        type: 'public-key',
                        transports: ['internal']
                    }],
                    userVerification: 'required',
                    timeout: 60000
                }
            });

            if (credential) {
                // Get stored session token
                const savedToken = localStorage.getItem('valnexo_session_token');
                const savedUser = localStorage.getItem('valnexo_biometric_user');
                
                if (savedToken && savedUser) {
                    return { 
                        success: true, 
                        token: savedToken,
                        email: savedUser
                    };
                }
            }
        } catch (e) {
            console.error('Biometric auth failed:', e);
            // If user cancelled or failed, don't disable
            if (e.name !== 'NotAllowedError') {
                return { success: false, error: e.message };
            }
            return { success: false, error: 'Authentification annulée' };
        } finally {
            setIsLoading(false);
        }
        
        return { success: false, error: 'Session expirée' };
    }, [isEnabled]);

    // Save session for biometric reauth
    const saveSessionForBiometric = useCallback((token) => {
        if (isEnabled) {
            localStorage.setItem('valnexo_session_token', token);
        }
    }, [isEnabled]);

    // Disable biometric auth
    const disableBiometric = useCallback(() => {
        localStorage.removeItem('valnexo_credential_id');
        localStorage.removeItem('valnexo_biometric_enabled');
        localStorage.removeItem('valnexo_biometric_user');
        localStorage.removeItem('valnexo_session_token');
        setIsEnabled(false);
    }, []);

    return {
        isAvailable,
        isEnabled,
        isLoading,
        isMobile: isMobile(),
        isStandalone: isStandalone(),
        enableBiometric,
        authenticateWithBiometric,
        saveSessionForBiometric,
        disableBiometric
    };
}
